import { Text, View } from '@react-pdf/renderer';

import { List } from '../components/List.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles, gridStyles, reconciliationPageStyles } from '../styles.ts';
import type { ReconciliationPageProps } from '../types.ts';

export const ReconciliationPage = ({
  subjectAddress,
  reconciliationCommentary,
  highestAndBestUseCommentary,
  indicatedValueByCostApproach,
  minCompMarketAdjustedSalePrice,
  maxCompMarketAdjustedSalePrice,
  averageCompMarketAdjustedSalePrice,
  medianCompMarketAdjustedSalePrice,
  averageCompDaysOnMarket,
  medianCompDaysOnMarket,
  alternativeComparablesCommentary,
  totalCostToCure,
  opinionOfMarketValue,
  revisionCommentary,
  isRevision,
  revisionType,
  commentaryV2,
  alerts,
  revisionData,
}: ReconciliationPageProps) => {
  const shouldDisplayAlertCommentarySection = alerts
    .filter(({ status }) => status === 'dismissed')
    .some(({ type }) => commentaryV2.alerts?.[type]);

  type PriceValue = { toString: (withSymbol?: boolean) => string };

  const isPriceValue = (value: unknown): value is PriceValue =>
    typeof value === 'object' &&
    value !== null &&
    typeof (value as PriceValue).toString === 'function';
  const priceToString = (value: unknown, withSymbol = true) =>
    isPriceValue(value) ? value.toString(withSymbol) : 'N/A';

  const daysToString = (value: unknown) =>
    typeof value === 'number' && Number.isFinite(value) ? `${value} days` : 'N/A';

  const totalCostToCureDisplay = priceToString(totalCostToCure, false);
  const opinionOfMarketValueDisplay = priceToString(opinionOfMarketValue, false);
  const indicatedValueByCostApproachDisplay = priceToString(indicatedValueByCostApproach, true);
  const adjustedSalesPriceRangeDisplay = (() => {
    const hasMin = isPriceValue(minCompMarketAdjustedSalePrice);
    const hasMax = isPriceValue(maxCompMarketAdjustedSalePrice);

    if (hasMin && hasMax) {
      return `${minCompMarketAdjustedSalePrice.toString(true)} - ${maxCompMarketAdjustedSalePrice.toString(true)}`;
    }
    if (hasMin) {
      return `${minCompMarketAdjustedSalePrice.toString(true)} - N/A`;
    }
    if (hasMax) {
      return `N/A - ${maxCompMarketAdjustedSalePrice.toString(true)}`;
    }
    return 'N/A';
  })();
  const averageComparableAdjustedSalePriceDisplay = priceToString(
    averageCompMarketAdjustedSalePrice,
  );
  const medianComparableAdjustedSalePriceDisplay = priceToString(medianCompMarketAdjustedSalePrice);
  const averageComparableDaysOnMarketDisplay = daysToString(averageCompDaysOnMarket);
  const medianComparableDaysOnMarketDisplay = daysToString(medianCompDaysOnMarket);

  return (
    <Tkv2PDFPage title="Reconciliation" address={subjectAddress}>
      {isRevision && (
        <>
          <View style={reconciliationPageStyles.section}>
            <Text style={fontStyles.h5}>{revisionType} Commentary</Text>
            <Text style={fontStyles.body}>{revisionCommentary?.replace('\n', '\n\n')}</Text>
          </View>
          {alternativeComparablesCommentary?.map((commentary, index) => {
            const comparableKey =
              revisionData?.comparables?.[index]?.address ?? `alternative-comparable-${index}`;
            return (
              <View key={comparableKey} style={reconciliationPageStyles.section}>
                <Text style={fontStyles.bodyBold}>
                  Alternative Comparable {index + 1}:{' '}
                  {revisionData?.comparables?.[index]?.address || 'N/A'}
                </Text>
                <Text style={fontStyles.body}>{commentary?.replace('\n', '\n\n')}</Text>
              </View>
            );
          })}
        </>
      )}

      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Opinion of Value</Text>
        <View
          style={{
            ...gridStyles.cardContainer,
            paddingHorizontal: 16,
            paddingVertical: 12,
            gap: 12,
          }}
        >
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Total Cost to Cure</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{totalCostToCureDisplay}</Text>
            </View>
          </View>
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Opinion of Market Value</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{opinionOfMarketValueDisplay}</Text>
            </View>
          </View>
        </View>
        <View style={{ marginTop: 8 }}>
          <Text style={fontStyles.small}>
            The opinion of market value stated in this report is based on the defined scope of work,
            statement of assumptions and limiting conditions, and appraiser&apos;s certification.
          </Text>
        </View>
      </View>
      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Values as Indicated</Text>
        <View
          style={{
            ...gridStyles.cardContainerMuted,
            paddingHorizontal: 16,
            paddingVertical: 12,
            gap: 12,
          }}
        >
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Indicated Value by Cost Approach</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{indicatedValueByCostApproachDisplay}</Text>
            </View>
          </View>
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Adjusted Sales Price Range</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{adjustedSalesPriceRangeDisplay}</Text>
            </View>
          </View>
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Average Comparable Adjusted Sale Price</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{averageComparableAdjustedSalePriceDisplay}</Text>
            </View>
          </View>
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Median Comparable Adjusted Sale Price</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{medianComparableAdjustedSalePriceDisplay}</Text>
            </View>
          </View>
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Average Comparable Days on Market</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{averageComparableDaysOnMarketDisplay}</Text>
            </View>
          </View>
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Median Comparable Days on Market</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{medianComparableDaysOnMarketDisplay}</Text>
            </View>
          </View>
        </View>
      </View>
      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Reconciliation Commentary</Text>
        <Text style={fontStyles.body}>{reconciliationCommentary?.replace('\n', '\n\n')}</Text>
      </View>
      <View style={reconciliationPageStyles.section}>
        <Text style={fontStyles.h5}>Highest & Best Use Commentary</Text>
        <Text style={fontStyles.body}>{highestAndBestUseCommentary?.replace('\n', '\n\n')}</Text>
      </View>
      {shouldDisplayAlertCommentarySection && (
        <View style={{ ...gridStyles.gridSection, marginRight: 20 }}>
          <Text style={fontStyles.h5}>Notes</Text>
          <List
            type="number"
            items={alerts
              .filter(({ status }) => status === 'dismissed')
              .map(({ type, message }) => (
                <Text key={type} style={fontStyles.body}>
                  <Text style={fontStyles.bodyBold}>{message}&nbsp;</Text>
                  {commentaryV2.alerts?.[type]}
                </Text>
              ))}
          />
        </View>
      )}
    </Tkv2PDFPage>
  );
};
